/**
 * @file components/biz/HooksForm
 * @author sunwen05
 */

import _ from 'lodash';
import classNames from 'classnames';
import React, {useContext, useEffect} from 'react';
import {Form, Steps} from 'acud';

import DuFormItem from './components/DuFormItem';
import RelationFormItems from './components/RelationFormItems';
import {ConfigContext} from '../../common/ConfigProvider';
import {DU_PREFIX} from '../../_common/constants';
import usePrevious from '../../../hooks/common/previous';

import {HooksFormProps, StepControls, DuFormItemControl} from '../../../hooks/biz/form/interface';

import './index.less';

const {Step} = Steps;
export const CLS_PREFIX = `${DU_PREFIX}-form`;
export const STEP_CLS_PREFIX = `${DU_PREFIX}-step-form`;

export interface DuFormItemProps {
    control: any;
    option: any;
    formData?: any;
    [fetch: string]: any;
}

export interface StepFormConfig {
    prevText?: string;
    prevClick?: () => boolean;
    nextText?: string;
    nextClick?: (formData: object) => boolean;
    endText?: string;
    isSavedRealTime?: boolean;
}

export type DISPLAY_ITEM_RELATION = '&&' | '||' | '' | undefined;
export type DISPLAY_ITEM_EXP = '===' | '!==' | '>' | '>=' | '<' | '<=' | 'in' | 'not-in' | 'custom';
export interface OnDisplayItemContent {
    relation?: DISPLAY_ITEM_RELATION;
    exp: DISPLAY_ITEM_EXP;
    value: any;
    customMethod?: () => boolean;
}

export interface OnDisplayItem {
    name: string; // 与DuFormItemControl的name对应
    relation?: DISPLAY_ITEM_RELATION;
    content: OnDisplayItemContent[];
}

export default (props: HooksFormProps) => {
    const {prefixCls: defaultPrefixCls, form: formConfigs} = useContext(ConfigContext);
    // props
    const {
        prefixCls: customizePrefixCls,
        className,
        title,
        description,
        form,
        // controls
        controlsConfig = [],
        // formData
        formData = {},
        // submit
        doSubmit,
        // step
        type,
        stepIndex = 0,
        stepButtonConfig,
        // options
        formItemOptionMerge,
        formExtraConfig = {},
        moreSettingCollapsed,
        defaultValueForceUpdate = formConfigs?.defaultValueForceUpdate
    } = props;

    const prevFormData = usePrevious(formData) || {};

    const prefixCls = customizePrefixCls
        || (defaultPrefixCls ? defaultPrefixCls + '-hooks-form' : '')
        || CLS_PREFIX;
    const stepPrefixCls = customizePrefixCls
        || (defaultPrefixCls ? defaultPrefixCls + '-hooks-step-form' : '')
        || STEP_CLS_PREFIX;

    // step渲染
    const stepTpl = _.map(controlsConfig as StepControls[], (step: StepControls) => (
        <Step key={step.title} title={step.title} />
    ));

    // classname
    const baseFormCls = classNames(className, prefixCls);
    const stepFormItemCls = `${stepPrefixCls}-item ${stepPrefixCls}-index-${stepIndex}`;

    // formData stringify
    const formDataStr = (() => {
        try {
            return JSON.stringify(formData);
        }
        catch (error) {
            return formData;
        }
    })();

    useEffect(() => {
        if (!defaultValueForceUpdate) {
            return;
        }
        const formValues = form.getFieldsValue();
        const dataToUpdate = {};
        for (let key in formData) {
            if (prevFormData[key] === undefined && formValues[key] === undefined) {
                dataToUpdate[key] = formData[key];
            }
        }
        !_.isEmpty(dataToUpdate) && form.setFieldsValue(dataToUpdate);
    }, [formDataStr, form]); // formData变化时强制更新Form组件数据

    return (
        <div className={baseFormCls}>
            {title && (
                <h2 className="title">{title}</h2>
            )}
            {description && <div className="description">{description}</div>}
            {type === 'step' ? (
                <Form
                    form={form}
                    initialValues={formData}
                    className={STEP_CLS_PREFIX}
                    onFinish={doSubmit}
                    {...formExtraConfig}
                >
                    {stepTpl && (
                        <Steps current={stepIndex}>{stepTpl}</Steps>
                    )}
                    <div className={stepFormItemCls}>
                        <RelationFormItems
                            prefixCls={prefixCls}
                            form={form}
                            option={formItemOptionMerge}
                            formData={formData}
                            controls={(controlsConfig[stepIndex] as StepControls).controls}
                            moreSettingCollapsed={moreSettingCollapsed}
                        />
                    </div>
                    <DuFormItem
                        prefixCls={prefixCls}
                        control={stepButtonConfig()}
                        option={formItemOptionMerge}
                    />
                </Form>
            ) : (
                <Form
                    form={form}
                    initialValues={formData}
                    onFinish={doSubmit}
                    {...formExtraConfig}
                >
                    <RelationFormItems
                        prefixCls={prefixCls}
                        form={form}
                        option={formItemOptionMerge}
                        formData={formData}
                        controls={(controlsConfig as DuFormItemControl[])}
                        moreSettingCollapsed={moreSettingCollapsed}
                    />
                </Form>
            )}
        </div>
    );
};
